import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend, LabelList,
} from "recharts";
import {
  Loader2, Download, Search, AlertTriangle, ShieldOff, UserX, Scale, FileSearch,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import PersonCard from "@/components/election/PersonCard";

const PRED = {
  KESIN: "Kesin bizden", BELKI: "Belki", BELIRSIZ: "Belirsiz", GELMEYECEK: "Gelmeyecek",
  ULASILAMADI: "Ulaşılamadı", KARSI: "Karşı", "": "Boş",
};
const SEV = {
  TERS: ["Ters oy (bize + karşı)", "bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-300"],
  BELIRSIZ: ["Belirsiz karışık", "bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300"],
  EKSIK: ["Bir kısmı gelmedi", "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300"],
  KARISIK: ["Karışık", "bg-sky-100 text-sky-800 dark:bg-sky-950 dark:text-sky-300"],
};
const TABS = [
  ["contradictions", "Çelişkiler", Scale],
  ["broken", "Söz verip karşıya verenler", ShieldOff],
  ["vague", "Söz verip belirsiz kalanlar", AlertTriangle],
  ["absent", "Söz verip gelmeyenler", UserX],
  ["search", "Kişi / firma ara", FileSearch],
];

export default function DeepAnalysis({ cno }) {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("contradictions");
  const [proxy, setProxy] = useState("all");
  const [kind, setKind] = useState("all");
  const [q, setQ] = useState("");
  const [sr, setSr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [person, setPerson] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/election-day/reconcile/deep", {
          params: cno && cno !== "all" ? { cno: Number(cno) } : {},
        });
        if (alive) setD(data);
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail, e));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [cno]);

  const doSearch = async () => {
    if (q.trim().length < 2) return toast.error("En az 2 karakter girin");
    setBusy(true);
    try {
      const { data } = await api.get("/election-day/reconcile/search", {
        params: { q: q.trim(), ...(cno && cno !== "all" ? { cno: Number(cno) } : {}) },
      });
      setSr(data);
      setTab("search");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, e));
    } finally {
      setBusy(false);
    }
  };

  const dl = async (scope, format) => {
    setBusy(true);
    try {
      const res = await api.get("/election-day/reconcile/export", {
        params: { scope, format, ...(cno && cno !== "all" ? { cno: Number(cno) } : {}) },
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `muhakeme_${scope}.${format === "pdf" ? "pdf" : "xlsx"}`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch {
      toast.error("İndirilemedi");
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return <div className="flex items-center gap-2 p-6 text-muted-foreground" data-testid="deep-loading">
      <Loader2 className="h-4 w-4 animate-spin" /> Derin analiz hazırlanıyor...
    </div>;
  }
  if (!d) return null;

  const fit = (r) => (proxy === "all" || (proxy === "var" ? r.proxy_document : !r.proxy_document))
    && (kind === "all" || (kind === "tuzel" ? r.entity_class !== "sahis" : r.entity_class === "sahis"));

  const list = tab === "contradictions"
    ? d.contradictions.filter((p) => p.firms.some(fit))
    : tab === "search" ? [] : (d[tab] || []).filter(fit);

  const st = d.stats;
  const chart = (d.by_committee || []).map((c) => ({
    k: `K${c.committee_no}`, name: c.name,
    "Karşıya verdi": c.broken, Belirsiz: c.vague, Gelmedi: c.absent,
  }));

  const FirmTable = ({ rows }) => (
    <ScrollArea className="mt-3 h-[460px]">
      <table className="w-full text-xs">
        <thead className="sticky top-0 bg-muted">
          <tr className="text-left">
            {["Firma", "Komite", "Oy kullanacak kişi", "Oy gücü", "Tür", "Belge",
              "2026 Tahmini", "Sandıkta", "Neden / Not"].map((h) => (
              <th key={h} className="whitespace-nowrap px-2 py-1.5 font-semibold">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className="border-b border-border/60" data-testid={`deep-row-${r.id}`}>
              <td className="max-w-[230px] px-2 py-1.5">
                <span className="block truncate font-medium">{r.company_name}</span>
                <span className="text-[10px] text-muted-foreground">{r.sicil_no}</span>
              </td>
              <td className="whitespace-nowrap px-2 py-1.5">#{r.committee_no}</td>
              <td className="max-w-[150px] px-2 py-1.5">
                {r.voting_person ? (
                  <button type="button" onClick={() => setPerson(r.voting_person)}
                    data-testid={`deep-person-${r.id}`}
                    className="block max-w-full truncate text-left font-medium text-sky-700 hover:underline dark:text-sky-400">
                    {r.voting_person}
                  </button>
                ) : "—"}
              </td>
              <td className="px-2 py-1.5">
                {r.vote_power > 1
                  ? <span className="rounded bg-[#0F2C59] px-1.5 py-0.5 font-bold text-white">{r.vote_power}</span>
                  : (r.vote_power || "—")}
              </td>
              <td className="whitespace-nowrap px-2 py-1.5">{r.entity_class === "sahis" ? "Şahıs" : "Tüzel"}</td>
              <td className="whitespace-nowrap px-2 py-1.5">{r.proxy_document ? "Var" : "—"}</td>
              <td className="whitespace-nowrap px-2 py-1.5">{PRED[r.prediction] ?? r.prediction}</td>
              <td className="whitespace-nowrap px-2 py-1.5 font-semibold"
                style={{ color: !r.marked ? "#64748B" : r.tally_group === "karsi" ? "#DC2626" : r.tally_group === "biz" ? "#16A34A" : "#F59E0B" }}>
                {r.marked ? (PRED[r.tally] ?? r.tally ?? "—") : "Gelmedi"}
              </td>
              <td className="max-w-[170px] truncate px-2 py-1.5 text-muted-foreground">
                {(r.reason || "") + (r.note ? ` · ${r.note}` : "") || "—"}
              </td>
            </tr>
          ))}
          {!rows.length && (
            <tr><td colSpan={9} className="px-2 py-3 text-muted-foreground">Kayıt yok.</td></tr>
          )}
        </tbody>
      </table>
    </ScrollArea>
  );

  return (
    <div className="space-y-4" data-testid="deep-analysis">
      {person && <PersonCard name={person} onClose={() => setPerson(null)} />}

      <Card className="p-4">
        <h3 className="flex items-center gap-1.5 text-sm font-bold">
          <FileSearch className="h-4 w-4" /> Derin muhakeme — kişi ve firma bazlı
        </h3>
        <p className="mt-0.5 text-xs text-muted-foreground">
          Bir kişinin farklı sandıklarda farklı davranıp davranmadığını, söz verip tersini
          yapanları ve hiç gelmeyenleri burada inceleyebilirsin.
        </p>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="pointer-events-none absolute left-2 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
            <input value={q} onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && doSearch()}
              placeholder="Kişi adı, firma ünvanı veya sicil no ara" data-testid="deep-search-input"
              className="h-9 w-full rounded-md border border-border bg-background pl-7 pr-2 text-sm" />
          </div>
          <Button size="sm" onClick={doSearch} disabled={busy} data-testid="deep-search-btn">
            <Search className="mr-1.5 h-4 w-4" /> Ara
          </Button>
        </div>
        <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {[
            ["Çelişkili kişi", st.contradiction_persons, `${st.contradiction_votes} oy etkiledi`, "#0F2C59"],
            ["Söz verip karşıya", st.broken.total, `${st.broken.belgeli} belgeli · ${st.broken.cok_oylu} çok oylu`, "#DC2626"],
            ["Söz verip belirsiz", st.vague.total, `${st.vague.belgeli} belgeli`, "#F59E0B"],
            ["Söz verip gelmeyen", st.absent.total, `${st.absent.belgesiz} belgesiz`, "#64748B"],
          ].map(([l, v, s, col]) => (
            <div key={l} className="rounded-lg border border-border p-3"
              style={{ borderLeftWidth: 4, borderLeftColor: col }} data-testid={`deep-stat-${l}`}>
              <p className="text-[11px] font-semibold uppercase text-muted-foreground">{l}</p>
              <p className="font-display text-2xl font-bold" style={{ color: col }}>{v}</p>
              <p className="text-[11px] text-muted-foreground">{s}</p>
            </div>
          ))}
        </div>
      </Card>

      {chart.length > 0 && (
        <Card className="p-4" data-testid="deep-chart">
          <h3 className="text-sm font-bold">En çok kayıp yaşanan komiteler</h3>
          <div className="mt-2" style={{ height: Math.max(260, chart.length * 26) }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chart} layout="vertical" margin={{ left: 8, right: 30, top: 4, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 11 }} />
                <YAxis type="category" dataKey="k" width={44} tick={{ fontSize: 11 }} />
                <Tooltip labelFormatter={(l) => chart.find((x) => x.k === l)?.name || l} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="Karşıya verdi" stackId="a" fill="#DC2626" />
                <Bar dataKey="Belirsiz" stackId="a" fill="#F59E0B" />
                <Bar dataKey="Gelmedi" stackId="a" fill="#64748B">
                  <LabelList dataKey="Gelmedi" position="right" fontSize={10} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      )}

      <Card className="p-4" data-testid="deep-tabs">
        <div className="flex flex-wrap gap-1.5">
          {TABS.map(([k, label, Icon]) => (
            <button key={k} type="button" onClick={() => setTab(k)} data-testid={`deep-tab-${k}`}
              className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${
                tab === k ? "bg-[#0F2C59] text-white" : "bg-muted text-muted-foreground hover:bg-muted/70"}`}>
              <Icon className="h-3.5 w-3.5" /> {label}
            </button>
          ))}
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-2">
          <span className="text-xs font-semibold text-muted-foreground">Yetki belgesi:</span>
          {[["all", "Tümü"], ["var", "Belgeli"], ["yok", "Belgesiz"]].map(([k, l]) => (
            <button key={k} type="button" onClick={() => setProxy(k)} data-testid={`deep-proxy-${k}`}
              className={`rounded-md px-2.5 py-1 text-xs font-semibold ${
                proxy === k ? "bg-[#0F2C59] text-white" : "bg-muted text-muted-foreground"}`}>{l}</button>
          ))}
          <span className="ml-2 text-xs font-semibold text-muted-foreground">Şirket türü:</span>
          {[["all", "Tümü"], ["tuzel", "Tüzel"], ["sahis", "Şahıs"]].map(([k, l]) => (
            <button key={k} type="button" onClick={() => setKind(k)} data-testid={`deep-kind-${k}`}
              className={`rounded-md px-2.5 py-1 text-xs font-semibold ${
                kind === k ? "bg-[#0F2C59] text-white" : "bg-muted text-muted-foreground"}`}>{l}</button>
          ))}
          {tab !== "search" && (
            <div className="ml-auto flex gap-2">
              <Button size="sm" variant="outline" disabled={busy} onClick={() => dl(tab, "xlsx")}
                data-testid={`deep-export-${tab}-xlsx`}>
                <Download className="mr-1.5 h-4 w-4" /> Excel
              </Button>
              <Button size="sm" variant="outline" disabled={busy} onClick={() => dl(tab, "pdf")}
                data-testid={`deep-export-${tab}-pdf`}>
                <Download className="mr-1.5 h-4 w-4" /> PDF
              </Button>
            </div>
          )}
        </div>

        {tab === "contradictions" && (
          <>
            <p className="mt-3 text-xs text-muted-foreground">
              Tüm oyları aynı olmayan kişiler — oy gücü çoktan aza sıralı. Her kişinin altında
              hangi sandıkta ne yaptığı firma firma görünür.
              {" "}Ters oy: {st.severity.TERS} kişi · Belirsiz karışık: {st.severity.BELIRSIZ} ·
              {" "}Bir kısmı gelmedi: {st.severity.EKSIK}
            </p>
            <ScrollArea className="mt-3 h-[520px]">
              <div className="space-y-2 pr-2">
                {list.map((p) => {
                  const [sl, sc] = SEV[p.severity];
                  return (
                    <div key={p.id} className="rounded-lg border border-border p-2"
                      data-testid={`contradiction-${p.id}`}>
                      <div className="flex flex-wrap items-center gap-2">
                        <button type="button" onClick={() => setPerson(p.person)}
                          className="font-semibold text-sky-700 hover:underline dark:text-sky-400"
                          data-testid={`contradiction-open-${p.id}`}>
                          {p.person}
                        </button>
                        <span className="rounded bg-[#0F2C59] px-1.5 py-0.5 text-[10px] font-bold text-white">
                          {p.power} oy
                        </span>
                        <span className={`rounded px-1.5 py-0.5 text-[10px] font-bold ${sc}`}>{sl}</span>
                        <span className="text-[11px] text-muted-foreground">
                          bize {p.outcomes.biz} · karşı {p.outcomes.karsi} ·
                          belirsiz {p.outcomes.belirsiz} · gelmedi {p.outcomes.gelmedi} ·
                          belgeli {p.proxy}/{p.power} · komiteler {p.committees.map((c) => `#${c}`).join(" ")}
                        </span>
                      </div>
                      <div className="mt-1.5 grid gap-1 text-[11px] sm:grid-cols-2">
                        {p.firms.filter(fit).map((r) => (
                          <div key={r.id} className="flex items-center gap-1.5 rounded bg-muted/50 px-2 py-1">
                            <span className="h-2 w-2 shrink-0 rounded-full" style={{
                              background: !r.marked ? "#64748B" : r.tally_group === "karsi" ? "#DC2626"
                                : r.tally_group === "biz" ? "#16A34A" : "#F59E0B",
                            }} />
                            <span className="min-w-0 flex-1 truncate">
                              #{r.committee_no} · {r.company_name}
                            </span>
                            <span className="shrink-0 font-semibold">
                              {PRED[r.prediction]} → {r.marked ? (PRED[r.tally] ?? "—") : "Gelmedi"}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
                {!list.length && (
                  <p className="py-3 text-xs text-muted-foreground">Çelişkili kayıt bulunamadı.</p>
                )}
              </div>
            </ScrollArea>
          </>
        )}

        {["broken", "vague", "absent"].includes(tab) && (
          <>
            <p className="mt-3 text-xs text-muted-foreground">
              {tab === "broken" && "Tahminde bize oy sözü veren (kesin/belki) ancak sandıkta karşı tarafa işaretlenen firmalar."}
              {tab === "vague" && "Kesin bizden sayılıp sandıkta belirsiz/nötr işaretlenen firmalar."}
              {tab === "absent" && "Bize oy sözü verip sandığa hiç gelmeyen firmalar."}
              {" "}Oy gücü çoktan aza sıralı · {list.length} kayıt gösteriliyor.
            </p>
            <FirmTable rows={list} />
          </>
        )}

        {tab === "search" && (
          <div className="mt-3">
            {!sr ? (
              <p className="text-xs text-muted-foreground">
                Yukarıdan kişi adı, firma ünvanı veya sicil no yazıp "Ara"ya bas.
              </p>
            ) : (
              <>
                <p className="text-xs text-muted-foreground">
                  «{sr.q}» için {sr.firm_count} firma, {sr.persons.length} kişi bulundu.
                </p>
                <div className="mt-2 space-y-2">
                  {sr.persons.map((p) => (
                    <div key={p.id} className="rounded-lg border border-border p-2"
                      data-testid={`search-person-${p.id}`}>
                      <div className="flex flex-wrap items-center gap-2">
                        <button type="button" onClick={() => setPerson(p.person)}
                          className="font-semibold text-sky-700 hover:underline dark:text-sky-400">
                          {p.person}
                        </button>
                        <span className="rounded bg-[#0F2C59] px-1.5 py-0.5 text-[10px] font-bold text-white">
                          {p.power} oy
                        </span>
                        {p.mixed && (
                          <span className="rounded bg-red-100 px-1.5 py-0.5 text-[10px] font-bold text-red-800 dark:bg-red-950 dark:text-red-300">
                            ÇELİŞKİLİ
                          </span>
                        )}
                        <span className="text-[11px] text-muted-foreground">
                          bize {p.outcomes.biz} · karşı {p.outcomes.karsi} ·
                          belirsiz {p.outcomes.belirsiz} · gelmedi {p.outcomes.gelmedi}
                        </span>
                      </div>
                      <div className="mt-1.5 grid gap-1 text-[11px] sm:grid-cols-2">
                        {p.firms.map((r) => (
                          <div key={r.id} className="flex items-center gap-1.5 rounded bg-muted/50 px-2 py-1">
                            <span className="min-w-0 flex-1 truncate">#{r.committee_no} · {r.company_name}</span>
                            <span className="shrink-0 font-semibold">
                              {PRED[r.prediction]} → {r.marked ? (PRED[r.tally] ?? "—") : "Gelmedi"}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
                <h4 className="mt-4 text-xs font-bold">Eşleşen firmalar</h4>
                <FirmTable rows={sr.firms.filter(fit)} />
              </>
            )}
          </div>
        )}
      </Card>
    </div>
  );
}
